import React, { useState, useEffect } from 'react';
import {
  Table,
  Header,
  Button,
  SpaceBetween,
  StatusIndicator,
  Box,
  Pagination,
  TextFilter,
  Spinner,
  Alert,
  Modal,
  ColumnLayout,
  Link,
} from '@cloudscape-design/components';
import { useCollection } from '@cloudscape-design/collection-hooks';
import { getOpenPOExports, UserExportRecord } from 'src/api/hooks/getOpenPOExports';
import { getOpenPOExport } from 'src/api/hooks/getOpenPOExport';
import { useFlashbar } from 'src/common/hooks/useFlashbar';

interface OpenPOExportsTableProps {
  controlId: string;
  onRefresh?: () => void;
}

const statusMap: Record<string, any> = {
  PENDING: 'pending',
  PROCESSING: 'in-progress',
  COMPLETED: 'success',
  FAILED: 'error',
};

const columnDefinitions = [
  {
    id: 'exportName',
    header: 'Export Name',
    cell: (item: UserExportRecord) => item.exportName || 'N/A',
    sortingField: 'exportName',
  },
  {
    id: 'status',
    header: 'Status',
    cell: (item: UserExportRecord) => (
      <StatusIndicator type={statusMap[item.status || 'PENDING']}>
        {item.status || 'PENDING'}
      </StatusIndicator>
    ),
    sortingField: 'status',
  },
  {
    id: 'exportType',
    header: 'Export Type',
    cell: (item: UserExportRecord) => item.exportType || 'CONTROL_DATA',
    sortingField: 'exportType',
  },
  {
    id: 'createdAt',
    header: 'Created At',
    cell: (item: UserExportRecord) =>
      item.createdAt ? new Date(item.createdAt).toLocaleString() : 'N/A',
    sortingField: 'createdAt',
  },
  {
    id: 's3Location',
    header: 'File Location',
    cell: (item: UserExportRecord) => (item.s3Location ? 'Available' : 'Not Available'),
    sortingField: 's3Location',
  },
];

export const OpenPOExportsTable: React.FC<OpenPOExportsTableProps> = ({ controlId, onRefresh }) => {
  const [selectedItems, setSelectedItems] = useState<UserExportRecord[]>([]);
  const [exports, setExports] = useState<UserExportRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [downloadUrls, setDownloadUrls] = useState<string[]>([]);
  const { addFlashbarMessage } = useFlashbar();

  const loadExports = async () => {
    setIsLoading(true);
    try {
      const response = await getOpenPOExports();
      console.log('Open PO exports response:', response);
      setExports(response.exports || []);
    } catch (error) {
      console.error('Error loading Open PO exports:', error);
      addFlashbarMessage({
        type: 'error',
        header: 'Error',
        content: 'Failed to load exports. Please try again.',
        dismissible: true,
        id: 'openpo_exports_error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadExports();
  }, []);

  const { items, actions, filteredItemsCount, collectionProps, filterProps, paginationProps } =
    useCollection(exports, {
      filtering: {
        empty: (
          <Box textAlign="center" color="inherit">
            <b>No exports</b>
            <Box variant="p" color="inherit">
              No Open PO exports to display.
            </Box>
          </Box>
        ),
        noMatch: (
          <Box textAlign="center" color="inherit">
            <b>No matches</b>
            <Box variant="p" color="inherit">
              We can't find a match.
            </Box>
            <Button onClick={() => actions.setFiltering('')}>Clear filter</Button>
          </Box>
        ),
      },
      pagination: { pageSize: 10 },
      sorting: {
        defaultState: {
          sortingColumn: columnDefinitions[3], // createdAt
          isDescending: true,
        },
      },
      selection: {},
    });

  const handleDownload = async () => {
    console.log('handleDownload called, selectedItems:', selectedItems);
    if (selectedItems.length > 0 && selectedItems[0].uuid) {
      try {
        console.log('Calling getOpenPOExport with uuid:', selectedItems[0].uuid);
        const exportDetails = await getOpenPOExport(selectedItems[0].uuid);
        console.log('Export details received:', exportDetails);

        // Handle multiple presigned URLs (new format)
        if (exportDetails.presignedUrls && exportDetails.presignedUrls.length > 1) {
          console.log('Multiple URLs case');
          setDownloadUrls(exportDetails.presignedUrls);
        }
        // Handle single presigned URL from array format
        else if (exportDetails.presignedUrls && exportDetails.presignedUrls.length === 1) {
          console.log('Single URL from array case');
          window.open(exportDetails.presignedUrls[0], '_blank');
          addFlashbarMessage({
            type: 'success',
            header: 'Download Started',
            content: 'Your export download has started.',
            dismissible: true,
            id: 'download_success',
          });
        }
        // Handle semicolon-separated URLs (backward compatibility)
        else if (exportDetails.presignedUrl && exportDetails.presignedUrl.includes(';')) {
          console.log('Semicolon-separated URLs case');
          const urls = exportDetails.presignedUrl.split(';').map((url) => url.trim());
          setDownloadUrls(urls);
        }
        // Handle single presigned URL (backward compatibility)
        else if (exportDetails.presignedUrl) {
          console.log('Single URL case, calling window.open with:', exportDetails.presignedUrl);
          window.open(exportDetails.presignedUrl, '_blank');
          addFlashbarMessage({
            type: 'success',
            header: 'Download Started',
            content: 'Your export download has started.',
            dismissible: true,
            id: 'download_success',
          });
        } else {
          console.log('No URL case');
          addFlashbarMessage({
            type: 'error',
            header: 'Download Failed',
            content: 'Export file is not ready for download.',
            dismissible: true,
            id: 'download_error',
          });
        }
      } catch (error) {
        console.error('Error downloading export:', error);
        addFlashbarMessage({
          type: 'error',
          header: 'Download Failed',
          content: 'Failed to download export. Please try again.',
          dismissible: true,
          id: 'download_error',
        });
      }
    } else {
      console.log('No selected items or no uuid');
    }
  };

  const handleExportCreated = () => {
    loadExports(); // Refresh the list
    if (onRefresh) onRefresh();
  };

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <>
      <Table
        {...collectionProps}
        selectionType="single"
        selectedItems={selectedItems}
        onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems)}
        loading={isLoading}
        loadingText="Loading exports..."
        header={
          <Header
            counter={`(${exports.length})`}
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button onClick={loadExports}>Refresh</Button>
                <Button
                  onClick={handleDownload}
                  variant="primary"
                  disabled={
                    selectedItems.length === 0 ||
                    selectedItems[0]?.status !== 'COMPLETED' ||
                    !selectedItems[0]?.s3Location
                  }
                >
                  Download
                </Button>
              </SpaceBetween>
            }
          >
            Open PO Dataset Exports
          </Header>
        }
        columnDefinitions={columnDefinitions}
        items={items}
        pagination={<Pagination {...paginationProps} />}
        filter={
          <TextFilter
            {...filterProps}
            countText={`${filteredItemsCount} ${filteredItemsCount === 1 ? 'match' : 'matches'}`}
            filteringPlaceholder="Find exports"
          />
        }
      />

      {/* Download Modal */}
      {downloadUrls.length > 0 && (
        <Modal
          visible={true}
          onDismiss={() => setDownloadUrls([])}
          header="Download Export Files"
          data-testid="download-modal"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={() => setDownloadUrls([])}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    downloadUrls.forEach((url, index) => {
                      setTimeout(() => {
                        window.open(url, '_blank');
                      }, index * 500);
                    });
                    setDownloadUrls([]);
                    addFlashbarMessage({
                      type: 'success',
                      header: 'Downloads Started',
                      content: `Started downloading ${downloadUrls.length} file(s).`,
                      dismissible: true,
                      id: 'download_success',
                    });
                  }}
                >
                  Download All
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          <SpaceBetween size="m">
            <Box>
              Your export contains {downloadUrls.length} CSV files. You can download individual
              files or all files at once.
            </Box>
            <ColumnLayout columns={1}>
              {downloadUrls.map((url, index) => (
                <Box key={index}>
                  <Link external href={url}>
                    📄 part-{String(index).padStart(5, '0')}.csv
                  </Link>
                </Box>
              ))}
            </ColumnLayout>
          </SpaceBetween>
        </Modal>
      )}
    </>
  );
};
